import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import {
    IBusinessPartner,
    ISalesOrder,
    ISalesOrderItem,
    IQuotation, IPaymentTerms,
} from "niteroi/packages/sales-distribution/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ListBase$DeleteEvent} from "sap/m/ListBase";
import Event from "sap/ui/base/Event";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import FilterType from "sap/ui/model/FilterType";
import Input, {Input$SuggestionItemSelectedEvent, Input$ValueHelpRequestEvent} from "sap/m/Input";
import TableSelectDialog, {
    TableSelectDialog$ConfirmEvent,
    TableSelectDialog$SearchEvent
} from "sap/m/TableSelectDialog";
import ColumnListItem from "sap/m/ColumnListItem";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";
import {AggregationBindingInfo} from "sap/ui/base/ManagedObject";

export default class Orders extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();
        this.initializeDocumentModels();
    }

    private initializeDocumentModels(): void {
        if (!this.getModel("salesDistributionSalesOrderDetail")) {
            this.setModel(new JSONModel({}), "salesDistributionSalesOrderDetail");
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");

        this.loadCollectionData("quotations", "salesDistributionQuotationCollection");
        this.loadCollectionData("business-partners", "salesDistributionBusinessPartnerCollection");

        if (routeName === "sales-distribution/orders/add") {
            this.getModel("salesDistributionSalesOrderDetail").setData(this.getBlankDocumentModel());
            this.getView().setBusy(false);
            return;
        } else {
            const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
            this.getData(id);
        }
    }

    protected getData(objectId: string = ""): void {
        const componentRoute = "/api/sales-distribution/orders";
        const url = objectId ? `${componentRoute}/${objectId}` : componentRoute;
        const headers = this.getRequestHeaders();
        const requestModel = this.getModel(objectId ? "salesDistributionSalesOrderDetail" : "salesDistributionSalesOrderCollection");
        requestModel.loadData(url, "", true, "GET", false, true, headers).then(() => {
            if (objectId) {
                this.getModel("salesDistributionBusinessPartnerDetail").setData(requestModel.getProperty("/businessPartner"));
                this.getModel("salesDistributionQuotationDetail").setData(requestModel.getProperty("/quotation"));
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idOrdersTable", ["salesOrderNumber", "salesOrderId", "status", "orderDate", "requestedDeliveryDate", "currency", "paymentTerms", "incoterm", "netValue", "soldToPartnerId", "createdAt"]);
    }

    protected getBlankDocumentModel(): ISalesOrder {
        return {
            salesOrderId: "",
            salesOrderNumber: "",
            status: "DRAFT",
            orderDate: "",
            requestedDeliveryDate: "",
            currency: "EUR",
            paymentTerms: "",
            incoterm: "",
            netValue: "0.00",
            createdAt: "",
            updatedAt: "",
            soldToPartnerId: "",
            businessPartner: null,
            quotationId: "",
            quotation: null,
            items: []
        };
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selectedDocument = rowContext?.getObject() as ISalesOrder | undefined;
        if (!selectedDocument?.salesOrderId) {
            return;
        }

        this.setDocumentModel(selectedDocument);
        this.navTo("sales-distribution/orders/edit", {id: selectedDocument.salesOrderId}, false);
    }

    protected setDocumentModel(document: ISalesOrder): void {
        let salesOrderModel = this.getModel("salesDistributionSalesOrderDetail");
        if (!salesOrderModel) {
            salesOrderModel = new JSONModel({});
            this.setModel(salesOrderModel, "salesDistributionSalesOrderDetail");
        }
        salesOrderModel.setData({...document});
    }

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/orders", {}, false);
    }

    public onCreateOrderPress(): void {
        this.setDocumentModel(this.getBlankDocumentModel());
        this.navTo("sales-distribution/orders/add", {}, true);
    }

    public onRefreshOrdersPress(): void {
        this.getModel("salesDistributionSalesOrderCollection")?.refresh(true);
    }

    public onPressSaveDraft(): void {
        this.getModel("salesDistributionSalesOrderDetail").setProperty("/status", "DRAFT");
        this.persist();
    }

    public onPressViewDocument(): void {
    }

    public onPressStartProcessing(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        model.setProperty("/status", "IN_PROCESS");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressMarkDelivered(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        model.setProperty("/status", "DELIVERED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressCloseOrder(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        model.setProperty("/status", "CLOSED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressRejectOrder(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        model.setProperty("/status", "REJECTED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressSaveDocument(): void {
        const salesOrderModel = this.getModel("salesDistributionSalesOrderDetail");

        const items = salesOrderModel.getProperty("/items") as Array<ISalesOrderItem>;
        if (!items || items.length === 0) {
            return;
        }
        const validItems = items.filter((item) => item.description && item.unitPrice && item.quantity && item.materialNumber && item.taxCode && item.unit);
        if (validItems.length === 0) {
            return;
        }
        salesOrderModel.setProperty("/items", validItems);

        const soldToPartnerId = salesOrderModel.getProperty("/soldToPartnerId");
        if (!soldToPartnerId) {
            return;
        }

        salesOrderModel.setProperty("/status", "OPEN");
        const createdAt = new Date().toISOString().replace("T", " ").split(".")[0];
        if (!salesOrderModel.getProperty("/orderDate")) {
            salesOrderModel.setProperty("/orderDate", createdAt.split(" ")[0]);
        }
        if (!salesOrderModel.getProperty("/createdAt")) {
            salesOrderModel.setProperty("/createdAt", createdAt);
        }
        salesOrderModel.setProperty("/updatedAt", createdAt);
        this.persist();
    }

    public persist(): void {
        this.getView().setBusy(true);

        const salesOrderModel = this.getModel("salesDistributionSalesOrderDetail");
        const salesOrderId = salesOrderModel.getProperty("/salesOrderId") as string;

        if (!salesOrderModel.getProperty("/soldToPartnerId")) {
            this.getView().setBusy(false);
            return;
        }

        const componentRoute = "/api/sales-distribution/orders";
        const url = salesOrderId ? `${componentRoute}/${salesOrderId}` : componentRoute;
        const method = salesOrderId ? "PUT" : "POST";
        salesOrderModel.loadData(url, salesOrderModel.getJSON(), true, method, false, true, this.getRequestHeaders()).then(() => {
            if (method === "POST") {
                this.navTo("sales-distribution/orders/edit", {id: salesOrderModel.getProperty("/salesOrderId")}, false);
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onPressAddItem(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        const modelData = model.getData() as ISalesOrder;
        const lastIndex = modelData.items.slice(-1);
        const nextItemNumber = lastIndex[0] ? (lastIndex[0].itemNumber + 10) : 10;
        const newItem: ISalesOrderItem = {
            salesOrderItemId: "",
            salesOrderId: "",
            itemNumber: nextItemNumber,
            materialNumber: "",
            description: "",
            quantity: "0",
            unit: "PCE",
            unitPrice: "0",
            taxCode: "A1"
        };
        modelData.items.push(newItem);
        model.refresh(true);
    }

    public onDeleteItem(event: ListBase$DeleteEvent): void {
        const source = event.getParameter("listItem");
        const bindingContext = source.getBindingContext("salesDistributionSalesOrderDetail");
        const model = bindingContext.getModel() as JSONModel;
        const salesOrderItemId = model.getProperty(bindingContext.getPath()).salesOrderItemId;
        const itemIndex = Number(bindingContext.getPath().split("/").pop());
        const modelData = model.getData() as ISalesOrder;
        const resolvedIndex = salesOrderItemId
            ? modelData.items.findIndex((item) => item.salesOrderItemId === salesOrderItemId)
            : itemIndex;
        if (resolvedIndex > -1) {
            modelData.items.splice(resolvedIndex, 1);
            model.refresh(true);
            this.onChangeLineItem();
        }
    }

    public onChangeLineItem(): void {
        const model = this.getModel("salesDistributionSalesOrderDetail");
        const modelData = model.getData() as ISalesOrder;
        let netValue = 0.00;
        modelData.items.forEach((item) => {
            if (item.unitPrice && item.quantity) {
                const itemNetValue = Number(item.unitPrice) * Number(item.quantity);
                netValue += Math.round((itemNetValue + Number.EPSILON) * 100) / 100;
            }
        });
        model.setProperty("/netValue", netValue.toFixed(2));
    }

    public onLineValueChange(): void {
    }

    public onValueHelpRequestPaymentTerms(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("niteroiSalesDistributionPaymentTermsDialog") as TableSelectDialog;
        if (dialog) {
            const input = event.getSource();
            dialog.open(input.getValue());
        }
    }

    public onSuggestionItemSelectedQuotation(event: Input$SuggestionItemSelectedEvent): void {
        const row = event.getParameter("selectedRow") as ColumnListItem;
        if (!row) {
            return;
        }
        const context = row.getBindingContext("salesDistributionQuotationCollection");
        if (context) {
            this._updateSalesOrderQuotation(context.getObject() as IQuotation);
        }
    }

    public onSuggestionItemSelectedPaymentTerms(event: Input$SuggestionItemSelectedEvent): void {
        const row = event.getParameter("selectedRow") as ColumnListItem;
        if (!row) {
            return;
        }
        const context = row.getBindingContext("salesDistributionCodes");
        if (context) {
            const paymentTerms = context.getObject() as IPaymentTerms;
            this.getModel("salesDistributionSalesOrderDetail").setProperty("/paymentTerms", paymentTerms.code);
        }
    }

    /**
     * Internal helper to update the sales order model state.
     */
    protected _updatePartner(partner: IBusinessPartner): void {
        const salesOrderModel = this.getView()?.getModel("salesDistributionSalesOrderDetail") as JSONModel;
        if (salesOrderModel) {
            salesOrderModel.setProperty("/soldToPartnerId", partner.businessPartnerId);
            this.getModel("salesDistributionBusinessPartnerDetail").setData(partner);
        }
    }

    private _updateSalesOrderQuotation(quotation: IQuotation): void {
        const salesOrderModel = this.getView()?.getModel("salesDistributionSalesOrderDetail") as JSONModel;
        if (salesOrderModel) {
            salesOrderModel.setProperty("/quotationId", quotation.quotationId);
            salesOrderModel.setProperty("/soldToPartnerId", quotation.soldToPartnerId);
            salesOrderModel.setProperty("/currency", quotation.currency);
            if (quotation.businessPartner.salesAreas.length > 0) {
                salesOrderModel.setProperty("/paymentTerms", quotation.businessPartner.salesAreas[0].paymentTerms);
            }

            // Copy quotation items to sales order items
            const newItems: ISalesOrderItem[] = [];
            quotation.items.forEach((quotationItem, index) => {
                const newItem: ISalesOrderItem = {
                    salesOrderItemId: "",
                    salesOrderId: "",
                    itemNumber: (index + 1) * 10,
                    materialNumber: quotationItem.materialNumber,
                    description: quotationItem.description,
                    quantity: quotationItem.quantity,
                    unit: quotationItem.unit,
                    unitPrice: quotationItem.unitPrice,
                    taxCode: quotationItem.taxCode
                };
                newItems.push(newItem);
            });

            salesOrderModel.setProperty("/items", newItems);


            const quotationModel = this.getModel("salesDistributionQuotationDetail");
            quotationModel.setData(quotation);

            const partnerModel = this.getModel("salesDistributionBusinessPartnerCollection");
            const partner = partnerModel.getData().find((partner: IBusinessPartner) => {
                return partner.businessPartnerId === quotation.soldToPartnerId;
            });
            this.getModel("salesDistributionBusinessPartnerDetail").setData(partner);
        }
    }

    public onPressBusinessPartnerLegalName(): void {
        (this.byId("soldToPartnerInput") as Input).fireValueHelpRequest();
    }

    public onPressQuotationNumber(): void {
        (this.byId("quotationIdInput") as Input).fireValueHelpRequest();
    }

    public onPressObjectQuotationNumber(): void {
        const quotationId = this.getModel("salesDistributionSalesOrderDetail").getProperty("/quotationId");
        if (quotationId) {
            this.navTo("sales-distribution/quotations/edit", {id: quotationId}, false);
        }
    }

    /**
     * Opens the Quotation Item Value Help Dialog
     */
    public onPressAddFromQuotation(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("quotationItemValueHelpDialog") as TableSelectDialog;
        if (dialog) {
            event.getSource().setBusy(true);
            const quotationModel = this.getModel("salesDistributionQuotationCollection");
            if (quotationModel) {
                quotationModel.loadData(
                    "/api/sales-distribution/quotations",
                    "",
                    true,
                    "GET",
                    true,
                    true,
                    this.getRequestHeaders()
                ).then(() => {
                    dialog.open(event.getSource().getValue());
                }).finally(() => {
                    event.getSource().setBusy(false);
                });
            }
        }
    }

    /**
     * Handles search in the Quotation Item Value Help Dialog
     */
    public onQuotationItemValueHelpSearch(event: TableSelectDialog$SearchEvent): void {
        const value = event.getParameter("value") || "";
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;

        // Filter for ACCEPTED quotations only
        const acceptedFilter = new Filter({filters: [
            new Filter("status", FilterOperator.EQ, "OPEN"),
            new Filter("status", FilterOperator.EQ, "SENT"),
            new Filter("status", FilterOperator.EQ, "ACCEPTED"),
        ], and: false});

        if (!value) {
            binding.filter(acceptedFilter);
            return;
        }

        const searchFilters = [
            new Filter("quotationNumber", FilterOperator.Contains, value),
            new Filter("soldToPartnerId", FilterOperator.Contains, value),
            new Filter("items/0/description", FilterOperator.Contains, value),
            new Filter("items/0/materialNumber", FilterOperator.Contains, value)
        ];

        const combinedSearchFilter = new Filter({filters: searchFilters, and: false});
        const finalFilter = new Filter({filters: [acceptedFilter, combinedSearchFilter], and: true});

        binding.filter([finalFilter]);
    }

    public onValueHelpConfirmPaymentTerms(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItems = event.getParameter("selectedItems");
        if (!selectedItems || selectedItems.length === 0) {
            this._resetQuotationItemValueHelpDialogFilters(event);
            return;
        }

        const context = selectedItems[0].getBindingContext("salesDistributionCodes");
        if (!context) {
            this._resetQuotationItemValueHelpDialogFilters(event);
            return;
        }

        const paymentTerms = context.getObject() as IPaymentTerms;
        this.getModel("salesDistributionSalesOrderDetail").setProperty("/paymentTerms", paymentTerms.code);
    }

    /**
     * Confirms selection from the Quotation Item Value Help Dialog
     */
    public onQuotationItemValueHelpConfirm(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItems = event.getParameter("selectedItems");
        if (!selectedItems || selectedItems.length === 0) {
            this._resetQuotationItemValueHelpDialogFilters(event);
            return;
        }

        // Get the selected quotation
        const context = selectedItems[0].getBindingContext("salesDistributionQuotationCollection");
        if (!context) {
            this._resetQuotationItemValueHelpDialogFilters(event);
            return;
        }

        const quotation = context.getObject() as IQuotation;
        this._updateSalesOrderQuotation(quotation);

        // Recalculate net value
        this.onChangeLineItem();

        this._resetQuotationItemValueHelpDialogFilters(event);
    }

    /**
     * Cleans up filters if the user cancels the quotation item dialog
     */
    public onQuotationItemValueHelpCancel(event: Event): void {
        this._resetQuotationItemValueHelpDialogFilters(event);
    }

    /**
     * Internal helper to reset quotation item dialog filters
     */
    private _resetQuotationItemValueHelpDialogFilters(event: Event): void {
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding | undefined;
        if (!binding) {
            return;
        }

        // Original filters as declared in the fragment's items binding
        const original = ((dialog.getBindingInfo("items") as AggregationBindingInfo)?.filters ?? []) as Filter | Filter[];

        binding.filter(original, FilterType.Application);
        binding.filter([], FilterType.Control); // clear any leftover search filter
    }
}
